import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription, Subject } from 'rxjs';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PdfFormDesignerComponent, FormElement, FormElementType, Signer, FormConfiguration, FormDesignerOptions } from '../../../../libs/form-designer/projects/pdf-form-designer/src/public-api';
import { NavigationService } from '../shared/common/NavigationEventService';
import { NavigationEvent } from '../shared/common/NavigationEvent';
import { ToolbarService } from '../shared/common/ToolbarEventService';
import { ToolbarEvent, ToolbarEventPageEnum } from '../shared/common/ToolbarEvent';
import { NotificationService } from '../shared/common/NotificationService';
import { FormElementService } from '../shared/common/FormElement.service';
import { findRecipientsMissingSignatureWidget } from '../shared/common/signing-method';
import * as Communication from '../communication/index';
import { Config } from '../../environments/environment';
import { Authorization } from '../shared/authorization/authorization.component';

@Component({
  selector: 'app-prepare-self',
  standalone: true,
  imports: [CommonModule, FormsModule, PdfViewerModule, TranslateModule, PdfFormDesignerComponent],
  templateUrl: './prepare-self.component.html',
  styleUrls: ['./prepare-self.component.css']
})
export class PrepareSelfComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('pdfDesigner') pdfDesigner!: PdfFormDesignerComponent;
  @ViewChild('toolbarContainer') toolbarContainer!: ElementRef;

  // PDF Configuration
  pdfSrc: any = {
    url: '',
    httpHeaders: {
      access_token: ''
    }
  };

  // Designer Configuration
  signers: Signer[] = [];
  initialConfiguration?: FormConfiguration;
  currentZoom: number = 1.0;
  isReadOnly: boolean = false;
  externalToolbar: boolean = true;

  // Designer Options - restrict elements for self-signing workflow with full-page layout
  private _designerOptions: FormDesignerOptions = {
    readOnly: false,
    allowedElements: [
      FormElementType.SIGNATURE,
      FormElementType.BIOMETRIC_SIGNATURE,
      FormElementType.INITIALS,
      FormElementType.TEXT,
    ] as FormElementType[],
    signers: [],
    // New full-page layout mode for better PDF display
    layoutMode: 'full-page',
    toolbarPosition: 'external', // Keep toolbar in external sidebar
    containerClass: 'prepare-self-designer', // Custom styling class
    toolbar: {
      enabled: true,
      external: true,
      showLabels: true,
      showIcons: true
    }
  };

  get designerOptions(): FormDesignerOptions {
    return this._designerOptions;
  }

  private updateDesignerOptions(): void {
    this._designerOptions = {
      ...this._designerOptions,
      readOnly: this.isReadOnly,
      signers: this.signers,
      activeSignerId: this.signers[0]?.id,
      toolbar: {
        ...this._designerOptions.toolbar,
        external: this.externalToolbar
      }
    };
  }

  // Job and Document Data
  jobIDParameter: number = 0;
  theJob: Communication.Job | any = null;
  recipientColor: string = '#3B82F6';
  isTemplateMode: boolean = false;
  isValidatingTemplate: boolean = false;

  // UI State
  toogleSidebar: boolean = false;
  pdfLoading: boolean = true;
  currentConfiguration?: FormConfiguration;
  changeOccured: boolean = false;
  isInitialLoading: boolean = true;
  isConfigChangeListenerActive: boolean = false;
  pendingConfigurationLoad?: FormConfiguration;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private navigationService: NavigationService,
    private toolbarService: ToolbarService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location,
    private authorization: Authorization,
    private notificationService: NotificationService,
    private jobsApi: Communication.JobsService,
    private documentsApi: Communication.DocumentsService,
    private fieldsApi: Communication.FieldsService,
    private templatesApi: Communication.TemplatesService,
    private formElementService: FormElementService,
    private translate: TranslateService
  ) {}

  /**
   * Validates if the job is actually a template using API endpoint
   * GOLDEN RULE: Prevents real jobs from being edited in template mode
   */
  private async validateTemplateJob(jobId: number): Promise<boolean> {
    try {
      this.isValidatingTemplate = true;
      const accessToken = this.authorization.getAccessToken();

      // Use GET /templates/{jobId} endpoint
      const template = await this.templatesApi.templateGet(accessToken, jobId).toPromise();

      console.log('[Template Validation] Job is a valid template:', template);
      return true;
    } catch (error) {
      // 400 error means it's NOT a template
      console.log('[Template Validation] Job is NOT a template:', error);
      return false;
    } finally {
      this.isValidatingTemplate = false;
    }
  }

  ngOnInit(): void {
    // Set locale for form designer based on current language from localStorage
    let locale: 'en' | 'tr' | 'nl' = 'en';
    try {
      const account = localStorage.getItem('account');
      if (account) {
        const accountData = JSON.parse(account);
        const accountLanguage = accountData.language;
        if (accountLanguage && ['en', 'tr', 'nl'].includes(accountLanguage)) {
          locale = accountLanguage as 'en' | 'tr' | 'nl';
        }
      }
    } catch (e) {
      console.warn('Could not read language from localStorage, using default:', e);
    }
    this._designerOptions = {
      ...this._designerOptions,
      locale: locale
    };

    // Subscribe to navigation events
    this.subscriptions.push(
      this.navigationService.eventOccured$.subscribe(event => this.onNavigationEvent(event))
    );

    // Subscribe to toolbar events (which are sent as NavigationEvents)
    // The toolbar service sends ToolbarEvents, but toolbar buttons send NavigationEvents

    // Load job from route parameters or fall back to sample data
    this.loadJobFromRoute();
  }

  ngAfterViewInit(): void {
    // External toolbar is handled by the pdf-form-designer component
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private setupSampleData(): void {
    console.log('Setting up sample data');

    // Sample signer for self-signing
    this.signers = [{
      id: 'self-signer',
      name: 'Self',
      email: 'user@example.com',
      color: this.recipientColor
    }];
    this.updateDesignerOptions();

    // Sample job data
    this.theJob = {
      recipients: [{
        name: 'Self',
        email: 'user@example.com',
        citizenId: null
      }]
    };

    // Use correct format for local sample PDF
    this.pdfSrc = {
      url: '/assets/sample1.pdf',  // Use the actual file that exists
      httpHeaders: {}  // No auth needed for local file
    };

    // Initial empty configuration
    this.initialConfiguration = {
      version: '3.0.0' as const,
      elements: [],
      pageCount: 1,
      signers: this.signers,
      activeSignerId: this.signers[0].id,
      metadata: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        author: 'prepare-self',
        coordinateSystem: 'PDF_EXACT_POINTS' as const
      }
    };

    this.pdfLoading = false;

    // Complete initial loading for sample data
    this.completeInitialLoading();
  }

  // =====================================
  // Event Handlers
  // =====================================
  onConfigurationChange(configuration: FormConfiguration): void {
    this.currentConfiguration = configuration;
    console.log('Configuration changed:', configuration);

    // Only save if we're not in initial loading state and listener is active
    if (!this.isInitialLoading && this.isConfigChangeListenerActive) {
      this.changeOccured = true;
      console.log('Auto-saving configuration changes...');
      this.saveConfiguration();
    } else {
      console.log('Skipping auto-save during initial loading');
    }
  }

  onPdfLoaded(event: any): void {
    console.log('PDF loaded:', event);
    this.pdfLoading = false;

    // Configure external toolbar styling programmatically (following documentation example)
    this.configureExternalToolbarStyling();

    // Check if we have a pending configuration to load
    if (this.pendingConfigurationLoad && this.pdfDesigner) {
      console.log('Loading pending configuration into designer');
      setTimeout(() => {
        this.pdfDesigner.loadConfiguration(this.pendingConfigurationLoad!);
        this.pendingConfigurationLoad = undefined;
      }, 100); // Small delay to ensure PDF is fully rendered
    }
  }

  /**
   * Configure external toolbar styling using CSS variables (documented approach)
   * Ensures button text is always dark and readable
   */
  private configureExternalToolbarStyling(): void {
    const toolbarElement = document.getElementById('external-toolbar');
    if (toolbarElement) {
      // Override parent styles that might force white text - following documentation
      toolbarElement.style.setProperty('--external-button-color', '#1a1a1a');
      toolbarElement.style.setProperty('--external-button-text-transform', 'none');
      toolbarElement.style.setProperty('--external-button-font-weight', 'normal');
      toolbarElement.style.setProperty('--external-button-font-size', '13px');

      // Hover states
      toolbarElement.style.setProperty('--external-button-hover-color', '#1a1a1a');

      // Active states remain as library default (white on blue)
      toolbarElement.style.setProperty('--external-button-active-color', 'white');
      toolbarElement.style.setProperty('--external-button-active-hover-color', 'white');

      // Disabled states
      toolbarElement.style.setProperty('--external-button-disabled-color', '#6c757d');

      console.log('External toolbar styling configured for dark text');
    }
  }

  onZoomChange(zoom: number): void {
    this.currentZoom = zoom;
  }

  private onNavigationEvent(event: NavigationEvent): void {
    if (!event || !event.acton) return;

    switch(event.acton) {
      case 'INCREMENT_ZOOM':
        this.incrementZoom(Number(event.value));
        break;

      case 'EDIT_RECIPIENTS':
        console.log('Edit recipients requested');
        // this.requestModalVisibility = true;
        // this.suspendSave = true;
        break;

      case 'CLOSE_REQUEST':
      case 'CLOSE':
        this.CloseRequest();
        break;

      case 'FINISH_SELF_SIGN':
        if (this.isTemplateMode) {
          // GOLDEN RULE: Block finish for templates
          this.notificationService.displayError({message: this.translate.instant("Templates cannot be sent. This is template editing mode.")});
          console.error('[Template Mode] Attempted to finish template - BLOCKED');
          return;
        }
        this.FinishSelfSign();
        break;

      case 'SAVE':
        console.log('Manual save requested');
        if (this.currentConfiguration) {
          this.saveConfiguration();
        }
        break;
    }
  }

  // =====================================
  // Toolbar Action Methods
  // =====================================
  private incrementZoom(delta: number): void {
    console.log(`Incrementing zoom by ${delta}`);

    // Use the PDF designer's zoom methods instead of manual calculation
    if (this.pdfDesigner) {
      if (delta > 0) {
        this.pdfDesigner.zoomIn();
      } else if (delta < 0) {
        this.pdfDesigner.zoomOut();
      }
    } else {
      console.warn('PDF designer not available for zoom control');
    }
  }

  private CloseRequest(): void {
    console.log('Close request received');

    // Save configuration before closing (following original pattern)
    if (this.currentConfiguration && this.changeOccured) {
      console.log('Saving configuration before closing...');
      this.saveConfiguration();

      // Navigate based on mode
      setTimeout(() => {
        if (this.isTemplateMode) {
          // Navigate back to templates list
          this.router.navigate(["/jobs"], {
            queryParams: { filter: 'Templates' }
          });
        } else {
          // Navigate back to previous page
          this.location.back();
        }
      }, 100);
    } else {
      // No changes to save, navigate immediately
      if (this.isTemplateMode) {
        this.router.navigate(["/jobs"], {
          queryParams: { filter: 'Templates' }
        });
      } else {
        this.location.back();
      }
    }
  }

  private FinishSelfSign(): void {
    console.log('Finish self sign requested');

    if (!this.theJob || !this.jobIDParameter) {
      console.error('No job available for finishing self-sign');
      return;
    }

    // Block self-sign when a bio/initials recipient has no matching signature widget.
    // QES and legacy-null recipients are exempt.
    const missingSignatureRecipients = findRecipientsMissingSignatureWidget(this.theJob?.recipients, this.currentConfiguration?.elements);
    if (missingSignatureRecipients.length) {
      this.notificationService.signatureAreaMissing(
        missingSignatureRecipients.map((r: any) => r.name || r.email || (r.id != null ? r.id.toString() : ''))
      );
      return;
    }

    // First save configuration if there are changes
    const saveAndFinish = () => {
      // Use default subject and message for self-signing
      const subject = "Self-signing completed";
      const message = "Document has been self-signed and is ready for processing.";

      // Call the REST API to finish the job
      this.subscriptions.push(
        this.jobsApi.jobsIdActionsFinishPut(
          this.authorization.getAccessToken(),
          this.jobIDParameter,
          subject,
          message,
          "true" // Self-sign flag
        ).subscribe(
          response => {
            console.log("Self-sign finish - status:", response.status);
            this.notificationService.signingRequestSent();
            setTimeout(() => {
              this.location.back();
            }, 100);
          },
          err => {
            console.error("Error finishing self-sign:", err);

            // Handle specific error cases like in original implementation
            try {
              const errorObject = JSON.parse(err._body || err.message);
              if (errorObject.code == 265) {
                this.notificationService.smsQuotaIsFull();
              } else {
                this.notificationService.displayError(err);
              }
            } catch (e) {
              // If parsing fails, just display the error
              this.notificationService.displayError(err);
            }
          }
        )
      );
    };

    // Save configuration first if there are changes
    if (this.changeOccured && this.currentConfiguration) {
      console.log('Saving configuration before finishing self-sign...');

      this.saveConfiguration();

      // Wait a moment for save to complete, then finish
      setTimeout(() => {
        saveAndFinish();
      }, 200);
    } else {
      // No changes to save, finish immediately
      saveAndFinish();
    }
  }

  /**
   * Save form configuration to API (following formDesigner pattern)
   */
  saveConfiguration(): void {
    if (!this.currentConfiguration || !this.theJob?.documents?.[0]) {
      console.log('Cannot save: missing configuration or job document');
      return;
    }

    // Convert FormElements to DocumentFields
    const signerMap = new Map([[this.signers[0]?.id || 'self-signer', this.theJob.recipients?.[0]?.id || null]]);
    const documentFields = this.formElementService.convertFormElementsToFields(
      this.currentConfiguration.elements,
      this.theJob.documents[0].id,
      signerMap
    );

    // Save to API
    const json: string = JSON.stringify(documentFields);
    this.subscriptions.push(
      this.fieldsApi.documentsIdFieldsPut(
        this.authorization.getAccessToken(),
        this.theJob.documents[0].id,
        json
      ).subscribe(
        (response: any) => {
          console.log("Form configuration saved to REST API");
          this.changeOccured = false;
        },
        (error: any) => {
          console.error("Error saving configuration:", error);
        }
      )
    );
  }

  // =====================================
  // Job and PDF Loading Methods
  // =====================================
  private loadJobFromRoute(): void {
    // Detect template mode from route
    const currentUrl = this.router.url;
    this.isTemplateMode = currentUrl.includes('/template-self/');
    console.log('[Template Mode] Enabled:', this.isTemplateMode);

    // Get job ID from route parameters
    this.activatedRoute.params.subscribe(async params => {
      if (params['id']) {
        this.jobIDParameter = +params['id'];
        console.log('Loading job ID:', this.jobIDParameter);

        // VALIDATION: Check if job type matches route mode
        if (this.isTemplateMode) {
          const isValidTemplate = await this.validateTemplateJob(this.jobIDParameter);

          if (!isValidTemplate) {
            // GOLDEN RULE ENFORCEMENT: Real job accessed via template route
            this.notificationService.displayError({message: this.translate.instant("This is not a template. Redirecting to prepare mode.")});
            this.router.navigate(['/prepare-self', this.jobIDParameter]);
            return;
          }
        } else {
          // Optional: Validate real jobs are not templates
          const isTemplate = await this.validateTemplateJob(this.jobIDParameter);

          if (isTemplate) {
            // GOLDEN RULE ENFORCEMENT: Template accessed via real job route
            this.notificationService.displayError({message: this.translate.instant("This is a template. Redirecting to template mode.")});
            this.router.navigate(['/template-self', this.jobIDParameter]);
            return;
          }
        }

        this.getTheJob();
      } else {
        console.log('No job ID found, using sample data');
        // Fallback to sample data if no ID
        this.setupSampleData();
      }
    });
  }

  private getTheJob(): void {
    this.subscriptions.push(
      this.jobsApi.jobsIdGet(this.authorization.getAccessToken(), this.jobIDParameter).subscribe(
        (response: Communication.Job) => {
          this.theJob = response;
          console.log('Job loaded:', response);

          // Use appropriate toolbar page based on mode
          const toolbarPage = this.isTemplateMode
            ? ToolbarEventPageEnum.TEMPLATE_PREPARE_SELF
            : ToolbarEventPageEnum.PREPARE_SELF;

          // Setup toolbar for PREPARE_SELF page
          this.toolbarService.action(new ToolbarEvent(
            toolbarPage,
            response.type || '',
            response.user?.id || 0,
            '',
            response.name || '',
            response.status || '',
            '',
            '',
            ''
          ));

          // Setup signer from job recipients
          if (response.recipients && response.recipients.length > 0) {
            this.setupSignerFromJob(response.recipients[0]);
          }

          // Load PDF document and existing fields
          if (response.documents && response.documents.length > 0) {
            const documentModel = response.documents[0];
            const fileURL = Config.WEBSERVICEURL + '/documents/' + documentModel.id + '/download';
            const accessToken = this.authorization.getAccessToken();

            this.loadFile(fileURL, accessToken);
            this.loadExistingFields(documentModel.id);
          }
        },
        error => {
          console.error('Error loading job:', error);
          // Fallback to sample data on error
          this.setupSampleData();
        }
      )
    );
  }

  private loadFile(fileURL: string, accessToken: string): void {
    console.log('Loading PDF from:', fileURL);
    const xhr = new XMLHttpRequest();
    xhr.open('HEAD', fileURL, false);
    xhr.setRequestHeader('access_token', accessToken);
    xhr.send();

    if (xhr.status == 200) {
      this.pdfSrc.url = fileURL;
      this.pdfSrc.httpHeaders.access_token = accessToken;
      this.pdfLoading = false;
      console.log('PDF loaded successfully');
    } else {
      console.error('Failed to load PDF, status:', xhr.status);
      this.pdfLoading = false;
      // Fallback to sample
      this.setupSampleData();
    }
  }

  private setupSignerFromJob(recipient: any): void {
    this.signers = [{
      id: recipient.id.toString(),
      name: recipient.name || 'Self',
      email: recipient.email,
      color: this.recipientColor
    }];
    this.updateDesignerOptions();

    // Create initial configuration with real signer
    this.initialConfiguration = {
      version: '3.0.0' as const,
      elements: [],
      pageCount: 1,
      signers: this.signers,
      activeSignerId: this.signers[0].id,
      metadata: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        author: 'prepare-self',
        coordinateSystem: 'PDF_EXACT_POINTS' as const
      }
    };
  }

  private loadExistingFields(documentId: number): void {
    console.log('Loading existing fields for document:', documentId);

    this.subscriptions.push(
      this.fieldsApi.documentsIdFieldsGet(this.authorization.getAccessToken(), documentId).subscribe(
        (response: Communication.DocumentField[]) => {
          console.log('Existing fields loaded:', response);

          // Convert fields to FormConfiguration and update initial configuration
          if (response && response.length > 0) {
            const recipientMap = new Map([[this.theJob.recipients?.[0]?.id || 0, this.signers[0]?.id || 'self-signer']]);
            const elements = this.formElementService.convertFieldsToFormElements(response, recipientMap);
            if (this.initialConfiguration) {
              this.initialConfiguration.elements = elements;
              console.log('Loading configuration with', elements.length, 'elements');

              // Call the pdf-form-designer component to load the configuration
              if (this.pdfDesigner && !this.pdfLoading) {
                console.log('Loading configuration immediately into designer');
                this.pdfDesigner.loadConfiguration(this.initialConfiguration);
              } else {
                console.log('PDF designer not ready, storing configuration for later load');
                this.pendingConfigurationLoad = this.initialConfiguration;
              }
            }
          }

          // Finish initial loading and activate configuration change listener
          this.completeInitialLoading();
        },
        (error: any) => {
          console.error('Error loading existing fields:', error);
          // Still complete loading even if no fields found
          this.completeInitialLoading();
        }
      )
    );
  }

  private completeInitialLoading(): void {
    console.log('Initial loading complete - activating configuration change listener');
    this.isInitialLoading = false;

    // Add a small delay to ensure the designer component has fully initialized
    setTimeout(() => {
      this.isConfigChangeListenerActive = true;
      console.log('Configuration change listener activated');
    }, 100);
  }

  // =====================================
  // UI Helper Methods
  // =====================================
  showSidebar(): void {
    this.toogleSidebar = !this.toogleSidebar;
    if (typeof window !== 'undefined' && (window as any).$) {
      (window as any).$('.sidebar').toggleClass('hidden-xs');
    }
  }

  /**
   * Download PDF with form design applied using the /documents/{id}/applyDesign endpoint
   */
  downloadDesignAppliedPDF(): void {
    if (!this.theJob?.documents?.[0]?.id) {
      this.notificationService.displayError('No document available for download');
      return;
    }

    const documentId = this.theJob.documents[0].id;
    const accessToken = this.authorization.getAccessToken();

    console.log('Downloading design-applied PDF for document:', documentId);

    this.subscriptions.push(
      this.documentsApi.documentsIdApplyDesignGet(accessToken, documentId).subscribe(
        (blob: Blob) => {
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `applied-design-${documentId}.pdf`;

          // Trigger download
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.notificationService.signingRequestSent(); // Use existing success method
          console.log('Design-applied PDF downloaded successfully');
        },
        (error: any) => {
          console.error('Error downloading design-applied PDF:', error);
          this.notificationService.displayError('Failed to download design-applied PDF');
        }
      )
    );
  }
}
